import _ from 'lodash';

import { SHORT_MONTH_DAY_YEAR_FORMAT, SHORT_TIME_FORMAT } from 'constants/date';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { getConditionalObject } from 'utils/data';
import { formatDate } from 'utils/date';

import {
  ACTOR_FILTER_SYSTEM_KEY,
  AUTOMATIC_FIELD_NOTE,
  ActorType,
  AUDIT_ACTOR_TYPE_BY_WIRE,
  AUDIT_OPERATION_BY_WIRE,
  AUDIT_OPERATION_LABEL,
  AUDIT_OPERATION_VARIANT,
  AUDIT_SUMMARY_UNAVAILABLE_KIND,
  EMPTY_FIELD_LIST,
  FIELDS_PILL_LIMIT,
  INCLUDE_SHADOWED_PARAMS,
  ON_BEHALF_OF_PREFIX,
  SERVICE_ACCOUNT_SUBLABEL,
  SYSTEM_ACTOR_LABEL,
  TRIGGERED_BY_PREFIX,
  VALUE_HIDDEN_PLACEHOLDER,
} from './EntityAuditHistory.constants';

/** Enough of the id to tell two actors apart without printing a full GUID. */
const ACTOR_ID_PREVIEW_LENGTH = 8;

const LOCAL_TZ = { includeLocalOffset: true };

/** How an actor cell is drawn: an initials avatar for a person, a glyph for a non-person. */
export const ActorKind = {
  HUMAN: 'human',
  SERVICE: 'service',
  SYSTEM: 'system',
};

export const toOperation = value => AUDIT_OPERATION_BY_WIRE[value] ?? value;

const toActorType = value => AUDIT_ACTOR_TYPE_BY_WIRE[value] ?? value;

export const getOperationLabel = operation => AUDIT_OPERATION_LABEL[operation] ?? operation;

export const getOperationVariant = operation => AUDIT_OPERATION_VARIANT[operation];

/**
 * The history endpoint pages with offset (`PagedResult`), but tolerate a bare array so an
 * AdminPanel that ships ahead of a host still deploying the paged shape renders the trail
 * rather than an empty tab.
 */
export const readHistoryPage = data =>
  Array.isArray(data)
    ? { items: data, page: 1, totalCount: data.length }
    : {
        items: data?.items ?? [],
        page: data?.page ?? 1,
        totalCount: data?.totalCount ?? 0,
      };

/**
 * A 404 on the history read means the endpoint predates its host deploy — a deployment state, not
 * a failure worth a red toast. Anything else is a genuine error the reader can retry.
 */
export const getHistoryErrorState = error => {
  const isUnavailable = _.get(error, 'response.status') === HTTP_RESPONSE_STATUS_CODES.NOT_FOUND;

  return { isUnavailable, hasFailed: !isUnavailable };
};

/** The flat row shows the full operator-local date + time (no date-group header). */
export const formatRowDateTime = occurredAtUtc =>
  occurredAtUtc
    ? `${formatDate(occurredAtUtc, SHORT_MONTH_DAY_YEAR_FORMAT, LOCAL_TZ)} · ${formatDate(
        occurredAtUtc,
        SHORT_TIME_FORMAT,
        LOCAL_TZ,
      )}`
    : '';

/** The MUI ascending-sort direction the header emits (`SORT_DIRECTIONS.ASC`). */
const ASCENDING_DIRECTION = 'asc';

/**
 * Order the loaded rows by occurrence time for the chosen direction (the header's sort). ISO
 * timestamps sort lexicographically, so a string compare is chronological.
 */
export const sortEntriesByTime = (entries, direction) =>
  _.orderBy(
    entries ?? [],
    [summary => summary.occurredAtUtc ?? ''],
    [direction === ASCENDING_DIRECTION ? 'asc' : 'desc'],
  );

const resolveActorName = (name, email, actorId) => {
  if (name || email) {
    return name || email;
  }

  return `User ${String(actorId).slice(0, ACTOR_ID_PREVIEW_LENGTH)}`;
};

/** A System actor carries no principal id. */
export const isSystemActor = summary =>
  toActorType(summary?.actorType) === ActorType.SYSTEM || !summary?.actorId;

/** Which of the three actor cases to draw (§ actor cases). */
export const getActorKind = summary => {
  if (isSystemActor(summary)) {
    return ActorKind.SYSTEM;
  }

  return toActorType(summary?.actorType) === ActorType.SERVICE
    ? ActorKind.SERVICE
    : ActorKind.HUMAN;
};

/**
 * The host returns an actor id (GUID) and type only, so a Human/Service actor's display name comes
 * from the `actorNames` lookup the container resolves. A System actor reads as "System"; an id the
 * lookup hasn't resolved yet falls back to a host-provided name/email or a shortened id.
 */
export const getActorLabel = (summary, actorNames = null) => {
  if (isSystemActor(summary)) {
    return SYSTEM_ACTOR_LABEL;
  }

  return (
    actorNames?.[summary?.actorId] ||
    resolveActorName(summary?.actorName, summary?.actorEmail, summary.actorId)
  );
};

/**
 * The actor's secondary line: a System row names its human initiator ("Triggered by …"), a Service
 * account says so, a person has none. Initiator-on-System is the only on-behalf-of case we draw.
 */
export const getActorSubline = (summary, actorNames = null) => {
  const kind = getActorKind(summary);

  if (kind === ActorKind.SERVICE) {
    return SERVICE_ACCOUNT_SUBLABEL;
  }

  if (kind === ActorKind.SYSTEM && summary?.initiatedByActorId) {
    const label =
      actorNames?.[summary.initiatedByActorId] ||
      resolveActorName(
        summary?.initiatedByActorName,
        summary?.initiatedByActorEmail,
        summary.initiatedByActorId,
      );

    return `${TRIGGERED_BY_PREFIX} ${label}`;
  }

  return '';
};

/** Up to two uppercase initials for a person's avatar. */
export const getInitials = name => {
  const words = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) {
    return '?';
  }

  return words
    .slice(0, 2)
    .map(word => word[0].toUpperCase())
    .join('');
};

/** "on behalf of …" — only when the initiator differs from the acting principal (drill-in header). */
export const getOnBehalfOfNote = (summary, actorNames = null) => {
  const initiatedBy = summary?.initiatedByActorId;

  if (!initiatedBy || initiatedBy === summary?.actorId) {
    return '';
  }

  const label =
    actorNames?.[initiatedBy] ||
    resolveActorName(summary?.initiatedByActorName, summary?.initiatedByActorEmail, initiatedBy);

  return `${ON_BEHALF_OF_PREFIX} ${label}`;
};

/** The field chips to draw in the row, plus how many more collapse into a "+N". */
export const splitChangedFields = (changedFields, limit = FIELDS_PILL_LIMIT) => {
  const fields = changedFields ?? [];

  return { shown: fields.slice(0, limit), overflow: Math.max(fields.length - limit, 0) };
};

/**
 * Diff values arrive as JSON scalars or structures. A null is a suppressed value (snapshotting
 * disabled for the type), rendered as "value hidden" rather than a literal null. Objects/arrays
 * render as inline JSON; scalars render as-is.
 */
export const formatAuditValue = value => {
  if (_.isNil(value)) {
    return VALUE_HIDDEN_PLACEHOLDER;
  }

  return _.isObject(value) ? JSON.stringify(value) : String(value);
};

/** A field can repeat across a diff, so the ordinal is what makes each row key collision-free. */
export const withChangeRowKeys = (auditId, changes) =>
  (changes ?? []).map((change, index) => ({
    ...change,
    key: `${auditId}:${change.field}:${index}`,
  }));

/**
 * The drill-in's changes split by `changePanels`: each panel takes the changes whose summary kind it
 * lists, in panel order, and the rest stay in the field diff. A panel with no change is dropped.
 */
export const splitPanelChanges = (changes, changePanels) => {
  const panels = (changePanels ?? [])
    .map(panel => ({
      ...panel,
      changes: changes.filter(change => panel.kinds.includes(change.summary?.kind)),
    }))
    .filter(panel => panel.changes.length > 0);

  return { fieldChanges: _.difference(changes, _.flatMap(panels, 'changes')), panels };
};

/** Fields that change on every save go last, so the edits someone made lead. */
export const sortAutomaticFieldsLast = (changes, automaticFields) =>
  _.sortBy(changes, change => automaticFields.includes(change.field));

/** The field panel's count: "3", or "3 — one of them automatic". */
export const getFieldChangesSubtitle = (changes, automaticFields) => {
  const count = changes.length;
  const automaticCount = changes.filter(change => automaticFields.includes(change.field)).length;

  if (automaticCount === 0) {
    return `${count}`;
  }

  if (automaticCount === count) {
    return `${count} — ${AUTOMATIC_FIELD_NOTE}`;
  }

  const share = automaticCount === 1 ? 'one of them' : `${automaticCount} of them`;

  return `${count} — ${share} ${AUTOMATIC_FIELD_NOTE}`;
};

/** How many fields the snapshot diff actually changed — one per recorded change. */
export const countDifferingFields = changes => (changes ?? []).length;

/** Read-only pretty-print for a before/after snapshot block. */
export const prettyPrintSnapshot = snapshot => JSON.stringify(snapshot ?? null, null, 2);

/** Matches the `"<key>":` at the start of a pretty-printed JSON line. */
const SNAPSHOT_KEY_PATTERN = /^\s*"([^"]+)":/;

/**
 * The pretty-printed snapshot split into lines, each flagged when its key is one of the changed
 * fields — so the before/after panes can highlight exactly what differs. The change list names
 * fields in PascalCase ("Name") while the snapshot keys are camelCase ("name"), so the match is
 * case-insensitive.
 */
export const getSnapshotLines = (snapshot, changedFields = []) => {
  const changed = new Set(changedFields.map(field => field.toLowerCase()));

  return prettyPrintSnapshot(snapshot)
    .split('\n')
    .map((text, index) => {
      const match = text.match(SNAPSHOT_KEY_PATTERN);

      return { key: index, text, isChanged: !!match && changed.has(match[1].toLowerCase()) };
    });
};

/**
 * The searchable text a row is matched against by the Fields-changed filter — the row's changed
 * field names joined into one string so the shared substring matcher (case-insensitive CONTAINS)
 * narrows the timeline to entries that touched a given property. A newline separator keeps the
 * substring match within a single field name rather than bridging two adjacent ones.
 */
export const getChangedFieldsFilterValue = summary => (summary?.changedFields ?? []).join('\n');

/** The value a row is filtered by in the Changed-by menu — its principal id, or one System bucket. */
export const getActorFilterKey = summary =>
  isSystemActor(summary) ? ACTOR_FILTER_SYSTEM_KEY : summary.actorId;

/** The distinct actors across the loaded rows, labelled as options for the Changed-by filter menu. */
export const buildActorFilterOptions = (entries, actorNames = null) => {
  const seen = new Set();

  return (entries ?? []).reduce((acc, summary) => {
    const value = getActorFilterKey(summary);

    if (seen.has(value)) {
      return acc;
    }

    seen.add(value);

    return [...acc, { value, label: getActorLabel(summary, actorNames) }];
  }, []);
};

/**
 * A predicate matching any field in the given lists. Case-insensitive, like getSnapshotLines: the
 * change list names fields PascalCase while the snapshot keys are camelCase.
 */
export const createFieldMatcher = (...fieldLists) => {
  const keys = new Set(
    fieldLists.flatMap(fields => fields ?? []).map(field => field.toLowerCase()),
  );

  return field => keys.has(field.toLowerCase());
};

/**
 * The fields hidden on one row: the consumer's list, or its `(summary, detail) => string[]` per-row
 * rule. `detail` is the drill-in's read, null in the timeline and until it arrives.
 */
export const resolveHiddenFields = (hiddenFields, summary, detail = null) => {
  if (!_.isFunction(hiddenFields)) {
    return hiddenFields;
  }

  return summary ? (hiddenFields(summary, detail) ?? EMPTY_FIELD_LIST) : EMPTY_FIELD_LIST;
};

/**
 * A `hiddenFields` rule for derived fields: `{ [derivedField]: sourceField }` hides each derived
 * field only on rows where its source changed too, so a row where only the derived one changed keeps it.
 */
export const createPairedHiddenFields = sourceByDerivedField => summary => {
  const isChanged = createFieldMatcher(summary?.changedFields);

  return Object.keys(sourceByDerivedField).filter(derivedField =>
    isChanged(sourceByDerivedField[derivedField]),
  );
};

/** The field names minus the hidden ones; the same array back when nothing is hidden. */
export const withoutFields = (fields, hiddenFields) => {
  if (_.isEmpty(fields) || _.isEmpty(hiddenFields)) {
    return fields;
  }

  const isHidden = createFieldMatcher(hiddenFields);

  return fields.filter(field => !isHidden(field));
};

export const withoutHiddenChanges = (changes, hiddenFields) => {
  if (_.isEmpty(changes) || _.isEmpty(hiddenFields)) {
    return changes;
  }

  const isHidden = createFieldMatcher(hiddenFields);

  return changes.filter(change => !isHidden(change.field));
};

/** A before/after image without the excluded keys (a missing image stays missing). */
export const omitSnapshotFields = (snapshot, isExcluded) =>
  _.isPlainObject(snapshot) ? _.omitBy(snapshot, (value, key) => isExcluded(key)) : snapshot;

/**
 * The consumer's renderer for a summarised change, or null for the "Field replaced" fallback. A
 * summary whose server summariser failed closed always falls back: it carries no data to render.
 */
export const getSummaryRenderer = (renderers, summary) =>
  summary.kind === AUDIT_SUMMARY_UNAVAILABLE_KIND ? null : (renderers?.[summary.kind] ?? null);

/**
 * The value under a field name in an object keyed by field (case-insensitively: change fields are
 * PascalCase, snapshot keys camelCase), or null.
 */
export const findByFieldName = (object, field) =>
  _.find(object, (value, key) => key.toLowerCase() === field.toLowerCase()) ?? null;

/** The consumer's value renderer for a change's field, or null. */
export const getFieldRenderer = (fieldRenderers, field) => findByFieldName(fieldRenderers, field);

export const getHistoryReadParams = includeShadowed =>
  getConditionalObject(includeShadowed, { params: INCLUDE_SHADOWED_PARAMS });

/** Row-action menu items without the hidden ones, stripped to what the menu item accepts. */
export const getVisibleRowActions = actions =>
  (actions ?? []).filter(action => !action.isHidden).map(action => _.omit(action, 'isHidden'));

/**
 * The consumer's extra drill-in tabs, each keyed by its stable `id` (prefixed so it cannot collide
 * with the built-in Changes / Snapshot tab values).
 */
export const buildDetailExtras = (renderDetailExtras, row, detail) =>
  detail && renderDetailExtras
    ? (renderDetailExtras(row, detail) ?? []).map(extra => ({
        ...extra,
        value: `extra:${extra.id}`,
      }))
    : [];

/** TableCDS row props flagging the rows a consumer decorates as muted; null leaves rows plain. */
export const createMutedRowProps = getRowDecoration =>
  getRowDecoration ? row => ({ 'data-row-muted': !!getRowDecoration(row)?.muted }) : null;

/** Identifies one drill-in read: the same row read with and without shadowed fields differs. */
export const getDetailRequestKey = (auditId, includeShadowed) => `${auditId}:${includeShadowed}`;
